"use client";
import { useEffect } from "react";
import { reportClientError, getErrorDedupKey, isErrorAlreadyReported, markErrorAsReported } from "@/lib/errorReporter";
import { usePathname } from "next/navigation";
import StackTrace from "stacktrace-js";

export default function GlobalErrorListener({ layout = "root" }) {
  const pathname = usePathname();
  useEffect(() => {
    
    const safeGetFrames = async (err) => {
      try {
        const stackFrames = await StackTrace.fromError(err);
        return stackFrames.map((f) => ({
          fileName: f.fileName,
          functionName: f.functionName,
          lineNumber: f.lineNumber,
          columnNumber: f.columnNumber,
        }));
      } catch {
        return [];
      }
    };

    const normalizeError = (message, source, lineno, colno, errorObj) => {
      // If errorObj is an Error, use it
      if (errorObj instanceof Error) return errorObj;
      // If message is an Error, use it
      if (message instanceof Error) return message;

      // If errorObj is an ErrorEvent, extract its error property
      if (errorObj && errorObj instanceof ErrorEvent) {
        if (errorObj.error instanceof Error) return errorObj.error;
        // If no error property, create a new Error with details
        const err = new Error(
          `ErrorEvent: ${errorObj.message || message || "Unknown error"}`
        );
        err.name = "ErrorEvent";
        err.stack = `at ${errorObj.filename || source || "unknown"}:${errorObj.lineno || lineno || "?"}:${errorObj.colno || colno || "?"}`;
        return err;
      }

      // If message is an ErrorEvent, extract its error property
      if (message && message instanceof ErrorEvent) {
        if (message.error instanceof Error) return message.error;
        const err = new Error(
          `ErrorEvent: ${message.message || "Unknown error"}`
        );
        err.name = "ErrorEvent";
        err.stack = `at ${message.filename || "unknown"}:${message.lineno || "?"}:${message.colno || "?"}`;
        return err;
      }

      // Fallback: create a generic Error
      const err = new Error(String(message || "Unknown error"));
      if (source || lineno || colno) {
        err.stack = `at ${source || "unknown"}:${lineno || "?"}:${colno || "?"}`;
      }
      return err;
    };

    const sendOnce = async (key, error, context) => {
      if (error && error.__alreadyReported) return; // skip if already reported
      if (isErrorAlreadyReported(key)) return; // already sent
      markErrorAsReported(key);
      
      await reportClientError(error, context);
      if (error && typeof error === "object") {
        error.__alreadyReported = true;
      }
    };

    const onError = async (message, source, lineno, colno, errorObj) => {
      const err = normalizeError(message, source, lineno, colno, errorObj);
      if (err && err.__alreadyReported) return; // skip if already reported
      const frames = await safeGetFrames(err);
      const context = {
        subject: "Unhandled Window Error",
        layout,
        route: typeof window !== "undefined" ? window.location.href : pathname,
        source,
        lineno,
        colno,
        resolved_frames: frames,
        error_source: "GlobalErrorListener.jsx",
      };
      const key = getErrorDedupKey(err, context);
      await sendOnce(key, err, context);
    };

    const onUnhandledRejection = async (event) => {
      const reason =
        event?.reason instanceof Error
          ? event.reason
          : new Error(String(event?.reason || "Unhandled rejection"));
      if (reason && reason.__alreadyReported) return; // skip if already reported
      const frames = await safeGetFrames(reason);
      const context = {
        subject: "Unhandled Promise Rejection",
        layout,
        route: typeof window !== "undefined" ? window.location.href : pathname,
        resolved_frames: frames,
        error_source: "GlobalErrorListener.jsx",
      };
      const key = getErrorDedupKey(reason, context);
      await sendOnce(key, reason, context);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [layout, pathname]);

  return null;
}
